import {
  collection,
  orderBy,
  query,
  serverTimestamp,
  doc,
  onSnapshot,
  runTransaction,
  Timestamp,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { firestore as db } from "../../../../backend/firebaseconfig";

const ANNOUNCEMENTS_COLLECTION = collection(
  db,
  "Marketing",
  "Announcements",
  "Items",
);

const normaliseEmail = (value = "") => {
  const rawValue =
    typeof value === "string"
      ? value
      : value && typeof value === "object"
        ? value.userEmail || ""
        : "";

  const email = String(rawValue || "")
    .trim()
    .toLowerCase();

  return email.includes("@") ? email : "";
};

export const subscribeAnnouncements = (onData, onError) => {
  const q = query(ANNOUNCEMENTS_COLLECTION, orderBy("createdAtMs", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const announcements = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      onData?.(announcements);
    },
    (error) => {
      //   console.error("Failed to listen to announcements:", error);
      onError?.(error);
    },
  );
};

export const subscribeUserAnnouncementAcknowledgements = (
  userEmail,
  callback,
  onError,
) => {
  const safeEmail = normaliseEmail(userEmail);

  if (!safeEmail) {
    callback?.([]);
    return () => {};
  }

  const acknowledgementsRef = collection(
    db,
    "Marketing",
    "Announcements",
    "UserAcknowledgements",
    safeEmail,
    "Items",
  );

  const q = query(acknowledgementsRef, orderBy("acknowledgedAtMs", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const ids = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return data.announcementId || docSnap.id;
      });

      callback?.(ids);
    },
    (error) => {
      console.error(
        "Failed to subscribe to announcement acknowledgements:",
        error,
      );
      onError?.(error);
    },
  );
};

export const acknowledgeAnnouncementForUser = async ({
  announcementId,
  userEmail,
  userRole = "",
  userName = "",
}) => {
  const safeEmail = normaliseEmail(userEmail);

  if (!announcementId || !safeEmail) return;

  const acknowledgedAt = Timestamp.now();
  const acknowledgedAtMs = Date.now();

  const announcementRef = doc(
    db,
    "Marketing",
    "Announcements",
    "Items",
    announcementId,
  );

  const userAcknowledgementRef = doc(
    db,
    "Marketing",
    "Announcements",
    "UserAcknowledgements",
    safeEmail,
    "Items",
    announcementId,
  );

  await runTransaction(db, async (transaction) => {
    const userAckSnap = await transaction.get(userAcknowledgementRef);

    // Already acknowledged, so do not increment again.
    if (userAckSnap.exists()) {
      return;
    }

    const acknowledgementDetails = {
      email: safeEmail,
      acknowledgedAt,
      acknowledgedAtMs,
    };

    transaction.set(
      userAcknowledgementRef,
      {
        announcementId,
        userEmail: safeEmail,
        userRole,
        userName,
        acknowledgementDetails,
        acknowledgedAt,
        acknowledgedAtMs,
        createdAt: serverTimestamp(),
      },
      { merge: true },
    );

    transaction.update(announcementRef, {
      clickCount: increment(1),
      acknowledgementCount: increment(1),
      lastAcknowledgedAt: serverTimestamp(),
      acknowledgementDetails: arrayUnion(acknowledgementDetails),
      updatedAt: serverTimestamp(),
    });
  });
};

export const trackAnnouncementResourceClickForUser = async ({
  announcementId,
  userEmail,
  userRole = "",
  userName = "",
  source = "",
  actionType = "",
  actionUrl = "",
}) => {
  const safeEmail = normaliseEmail(userEmail);

  if (!announcementId || !safeEmail) return;

  const clickedAt = Timestamp.now();
  const clickedAtMs = Date.now();

  const announcementRef = doc(
    db,
    "Marketing",
    "Announcements",
    "Items",
    announcementId,
  );

  const clickLogRef = doc(
    collection(
      db,
      "Marketing",
      "Announcements",
      "Items",
      announcementId,
      "ResourceClicks",
    ),
  );

  const userResourceClickRef = doc(
    db,
    "Marketing",
    "Announcements",
    "UserResourceClicks",
    safeEmail,
    "Items",
    announcementId,
  );

  await runTransaction(db, async (transaction) => {
    const userClickSnap = await transaction.get(userResourceClickRef);

    transaction.set(clickLogRef, {
      announcementId,
      userEmail: safeEmail,
      userRole,
      userName,
      source,
      actionType,
      actionUrl,
      clickedAt,
      clickedAtMs,
      createdAt: serverTimestamp(),
    });

    transaction.set(
      userResourceClickRef,
      {
        announcementId,
        userEmail: safeEmail,
        userRole,
        userName,
        source,
        actionType,
        actionUrl,
        firstClickedAt: userClickSnap.exists()
          ? userClickSnap.data()?.firstClickedAt || clickedAt
          : clickedAt,
        firstClickedAtMs: userClickSnap.exists()
          ? userClickSnap.data()?.firstClickedAtMs || clickedAtMs
          : clickedAtMs,
        lastClickedAt: clickedAt,
        lastClickedAtMs: clickedAtMs,
        clickCount: increment(1),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    transaction.update(announcementRef, {
      resourceClickCount: increment(1),
      lastResourceClickAt: serverTimestamp(),
      lastResourceClickedBy: safeEmail,
      resourceClickedUsers: arrayUnion(safeEmail),
      uniqueResourceClickUserCount: userClickSnap.exists()
        ? increment(0)
        : increment(1),
      updatedAt: serverTimestamp(),
    });
  });
};